import { useEventBus } from "@vueuse/core"
import { ComputedRef, computed, inject, onUnmounted, ref, unref } from "vue"
import * as _ from "lodash-es"
import * as Sentry from "@sentry/vue"

import {
    ConfigGraphCard,
    Indicator,
    DatasetBuildingVariable,
    ConditionBlock,
} from "@types"
import { formatDate } from "@core/helpers"
import {
    handleBuildingCondition,
    handleSourceCondition,
    handleUserCondition,
} from "@modules/data/helpers"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import {
    DataSourceByIdResponse,
    DataBuildingByIdResponse,
    Services,
} from "@services"
import { ServiceError } from "@core/exceptions"
import {
    evaluateVisibleConditionsBlocks,
    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
    blockOmitFromGraphSelector,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"

export const useKPI = (
    type: "building" | "source" = "building",
    config:
        | ComputedRef<Record<string, ConfigGraphCard> | null>
        | Record<string, ConfigGraphCard>
) => {
    const { $getDataSourceById, $getDataBuildingById } =
        inject<Services>("services")!

    const graphStore = useGraphStore()
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()

    const indicators = ref<
        {
            name: string
            indicator: Indicator[]
        }[]
    >([])
    const bus = useEventBus<string>("news")
    const error = ref(false)
    const loading = ref(false)

    const unsubscribe = bus.on((event: string) => {
        if (event === "update:indicator") {
            fetchKPIValue()
        }
    })
    /**
     * Liste des KPIs à afficher en fonction des graphiques sélectionnés
     */
    const getKPIList = computed(() => {
        if (!unref(config)) {
            return []
        }

        return Object.values(unref(config) as Record<string, ConfigGraphCard>)
            .filter((graph) => graphStore.graphs.includes(graph.id))
            .filter((graph) => {
                if (graph.visible_conditions?.length === 0) return true
                return evaluateVisibleConditionsBlocks(
                    graph.visible_conditions,
                    graph.visible_conditions_combine,
                    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
                    (condition: ConditionBlock) => {
                        if (condition.origin === "user") {
                            return handleUserCondition(condition)
                        }
                        if (type === "source") {
                            const source = graphStore.currentSourceId
                                ? sourceStore.getSourceById(
                                      graphStore.currentSourceId
                                  )
                                : undefined
                            if (!source) return false
                            return handleSourceCondition([source], condition)
                        }
                        if (type === "building") {
                            if (!buildingStore.building) return false

                            if (condition.origin === "building") {
                                return handleBuildingCondition(
                                    buildingStore.building,
                                    condition
                                )
                            }

                            return handleSourceCondition(
                                buildingStore.getSources,
                                condition
                            )
                        }
                        return false
                    }
                )
            })
            .flatMap((graph) => {
                return { name: graph.name, graph_list: graph.graph_list }
            })
            .flatMap((graph) => {
                return {
                    name: graph.name,
                    graph_list: (
                        graph.graph_list as ConfigGraphCard["graph_list"]
                    )
                        .filter((graph) => {
                            if (
                                graph.visible_conditions?.some((c) =>
                                    blockOmitFromGraphSelector(c)
                                )
                            ) {
                                return graph.visible_conditions.some(
                                    ({ conditions, ...blockRest }) => {
                                        if (
                                            !blockOmitFromGraphSelector(
                                                blockRest
                                            )
                                        ) {
                                            return true
                                        }
                                        return conditions.some((condition) => {
                                            if (condition.origin === "user") {
                                                return handleUserCondition(
                                                    condition
                                                )
                                            }
                                            if (
                                                condition.origin ===
                                                    "building" &&
                                                !!buildingStore.building
                                            ) {
                                                return handleBuildingCondition(
                                                    buildingStore.building,
                                                    condition
                                                )
                                            }
                                            const source =
                                                graphStore.currentSourceId
                                                    ? sourceStore.getSourceById(
                                                          graphStore.currentSourceId
                                                      )
                                                    : undefined
                                            return handleSourceCondition(
                                                source
                                                    ? [source]
                                                    : buildingStore.getSources,
                                                condition
                                            )
                                        })
                                    }
                                )
                            }
                            return true
                        })
                        .map((graph_list) => {
                            return {
                                ...graph_list,
                                datasets: graph_list.datasets.filter(
                                    (dataset) => dataset.type !== "annotation"
                                ),
                            }
                        }),
                }
            })
    })
    /**
     * Récupère les données des KPIs
     */
    async function fetchKPIValue() {
        // si déjà en cours de chargement, on ne fait rien
        if (loading.value) return

        const entityId =
            type === "building"
                ? buildingStore.building?._id
                : graphStore.currentSourceId
                  ? sourceStore.getSourceById(graphStore.currentSourceId)
                        ?._id_source
                  : undefined
        if (!entityId) {
            return
        }
        // Si pas de KPIs, on vide les indicateurs
        if (getKPIList.value.length === 0) {
            indicators.value = []
            return
        }
        const [periodFrom, periodTo] = graphStore.period
        const dateFrom = formatDate(periodFrom, "YYYY-MM-DD")
        const dateTo = formatDate(periodTo, "YYYY-MM-DD")
        if (!dateFrom || !dateTo) {
            return
        }
        const service =
            type === "building" ? $getDataBuildingById : $getDataSourceById

        try {
            loading.value = true
            error.value = false
            const variables = getKPIList.value.flatMap((kpiGroup) =>
                kpiGroup.graph_list
                    .flatMap((graph) => graph.datasets)
                    .flatMap((dataset) => dataset.type)
            )
            const { periode } = (await service(
                entityId,
                dateFrom,
                dateTo,
                graphStore.freq,
                variables
            )) as DataSourceByIdResponse | DataBuildingByIdResponse

            indicators.value = getKPIList.value.map((kpiGroup) => {
                return {
                    name: kpiGroup.name,
                    indicator: kpiGroup.graph_list.flatMap((graph) => {
                        return graph.datasets.flatMap((dataset) => {
                            const datasetTyped =
                                dataset as DatasetBuildingVariable

                            const kpiDataValue = _.get(
                                periode?.data,
                                datasetTyped.type,
                                NaN
                            ) as number

                            return {
                                graph_id: graph.id,
                                type: datasetTyped.type,
                                name: datasetTyped.name,
                                value:
                                    isNaN(kpiDataValue) || kpiDataValue === null
                                        ? "-"
                                        : kpiDataValue.toFixed(2),
                                unit: datasetTyped.unit,
                                icon: datasetTyped.icon,
                                icon_color: datasetTyped.icon_color,
                            }
                        })
                    }),
                }
            })
        } catch (e) {
            // Si l'erreur est de type "aucune-donnee" alors on ne log pas dans Sentry et ce n'est pas une erreur
            if (e instanceof ServiceError && e.code === "aucune-donnee") {
                indicators.value = getKPIList.value.map((kpiGroup) => {
                    return {
                        name: kpiGroup.name,
                        indicator: kpiGroup.graph_list.flatMap((graph) => {
                            return graph.datasets.flatMap((dataset) => {
                                const datasetTyped =
                                    dataset as DatasetBuildingVariable

                                return {
                                    graph_id: graph.id,
                                    type: datasetTyped.type,
                                    name: datasetTyped.name,
                                    value: "-",
                                    unit: datasetTyped.unit,
                                    icon: datasetTyped.icon,
                                    icon_color: datasetTyped.icon_color,
                                }
                            })
                        }),
                    }
                })
                return
            }

            Sentry.captureException(e, {
                extra: {
                    type,
                    _id: entityId,
                    period: graphStore.period,
                    freq: graphStore.freq,
                    kpiList: getKPIList.value,
                },
            })
            error.value = true
        } finally {
            loading.value = false
        }
    }
    /**
     * Gère le clic sur un KPI
     */
    function onClickKPI({
        type,
        graph_id,
    }: {
        type: string
        graph_id: string
    }) {
        // trouve l'indicateur correspondant
        const indicator = indicators.value
            ?.flatMap((group) => group.indicator)
            .find((indicator) => indicator.type === type)

        if (indicator) {
            bus.emit("graph:scroll", { type, graph_id: graph_id })
        }
    }

    onUnmounted(() => {
        unsubscribe()
    })

    return {
        indicators,
        getKPIList,
        error,
        loading,
        fetchKPIValue,
        onClickKPI,
    }
}
